import posthog from "posthog-js";

let initialized = false;

type Primitive = string | number | boolean | null;

export interface SolsticeEvents {
  solstice_view_opened: { dashboard_uid: string; dashboard_name: string };
  solstice_view_schedule_saved: { dashboard_uid: string };
  solstice_view_schedule_test_sent: {
    dashboard_uid: string;
    recipient_count: number;
  };
  solstice_view_schedule_removed: { dashboard_uid: string };
  solstice_view_downloaded: {
    dashboard_uid: string;
    dashboard_name: string;
    time_range: string;
    variables_set: number;
  };
  solstice_ticket_uploader_submitted: {
    image_count: number;
    from_draft: boolean;
  };
  solstice_ticket_reviewed: { grading_correct: boolean };
  solstice_export_run: {
    format: string;
    pivot: boolean;
    breakdown_type: string;
    time_grain: string;
  };
  solstice_frame_reports_downloaded: { camera_count: number };
  solstice_alert_reviewed: { grade: string; target: string };
  solstice_alert_config_toggled: { rule: string; enabled: boolean };
  solstice_facility_map_camera_opened: { camera_id: number };
  solstice_frame_viewer_opened: { source: "grid" };
  solstice_organization_switched: {
    from_organization_id: number | null;
    to_organization_id: number;
  };
}

export interface PostHogUser {
  id: string;
  email?: string | null;
  name?: string | null;
  organizationId?: number | null;
  organizationName?: string | null;
  isDemoViewer?: boolean;
}

export const isPostHogProjectKey = (key: string | undefined): key is string =>
  typeof key === "string" && key.startsWith("phc_");

export const initPostHog = (): void => {
  if (initialized) return;
  if (typeof window === "undefined") return;
  if (process.env.NODE_ENV === "development") return;
  if (["localhost", "127.0.0.1"].includes(window.location.hostname)) return;
  if (window.location.pathname.startsWith("/print/")) return;

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!isPostHogProjectKey(key)) {
    console.error(
      "PostHog disabled: NEXT_PUBLIC_POSTHOG_KEY is missing or not a phc_ project key.",
    );
    return;
  }

  posthog.init(key, {
    api_host: "/ingest",
    ui_host: "https://us.posthog.com",
    defaults: "2025-05-24",
    capture_pageview: "history_change",
    capture_pageleave: true,
    autocapture: true,
    capture_exceptions: false,
    disable_session_recording: true,
    person_profiles: "identified_only",
  });
  posthog.register({ app: "solstice" });
  initialized = true;
};

export const postHogIdentity = (user: PostHogUser) => {
  const personProperties: Record<string, Primitive> = {
    ...(user.email ? { email: user.email } : {}),
    ...(user.name ? { name: user.name } : {}),
    demo_viewer: Boolean(user.isDemoViewer),
  };
  const group =
    user.organizationId != null
      ? {
          key: String(user.organizationId),
          properties: user.isDemoViewer
            ? {}
            : user.organizationName
              ? { name: user.organizationName }
              : {},
        }
      : null;
  return { distinctId: user.id, personProperties, group };
};

export const setPostHogUser = (user: PostHogUser): void => {
  if (!initialized) return;
  const { distinctId, personProperties, group } = postHogIdentity(user);
  posthog.identify(distinctId, personProperties);
  if (group) posthog.group("organization", group.key, group.properties);
};

export const clearPostHogUser = (): void => {
  if (!initialized) return;
  posthog.reset();
};

export const track = <E extends keyof SolsticeEvents>(
  event: E,
  properties: SolsticeEvents[E],
  { beforeUnload = false }: { beforeUnload?: boolean } = {},
): void => {
  if (!initialized) return;
  posthog.capture(
    event,
    properties,
    beforeUnload ? { transport: "sendBeacon" } : undefined,
  );
};
